import {
	Button,
	CardActions,
	CardContent,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppRichTextEditor from '@/common/components/editor/AppRichTextEditor';
import { useUpdateHealthDetailMutation } from '@/api/app-setting/healthApiSlice';
import { HealthDetailRequest } from '@/api/DTO/app-setting/health.interface';

interface Props {
	type: FormType;
	details?: HealthDetailRequest;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function HealthInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [updateHealthDetail] = useUpdateHealthDetailMutation();

	const defaultValues: DefaultValues<HealthDetailRequest> = {
		contentZh: details?.contentZh || '',
		contentEn: details?.contentEn || '',

		tipsZh: details?.tipsZh || '',
		tipsEn: details?.tipsEn || '',
	};
	useEffect(() => {
		if (type == FormType.edit) {
			if (details?.contentZh) methods.setValue('contentZh', details.contentZh);
			if (details?.contentEn) methods.setValue('contentEn', details.contentEn);
			if (details?.tipsZh) methods.setValue('tipsZh', details.tipsZh);
			if (details?.tipsEn) methods.setValue('tipsEn', details.tipsEn);
		}
	}, [details]);

	const formSchema = Yup.object().shape({});
	const methods = useForm<HealthDetailRequest>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<HealthDetailRequest> = async (data) => {
		const obj: HealthDetailRequest = { ...data };
		const response = await updateHealthDetail({
			...obj,
			id: details?.id || '',
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.editContentSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			setType(FormType.view);
		}
	};

	const handleClose = () => {
		setType(FormType.view);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('moment.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('moment.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('health.resultContent_')}
										</Typography>
										<AppRichTextEditor
											control={methods.control}
											name='contentZh'
										/>
									</Grid>
									<Grid item xs={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('health.healthTips_')}
										</Typography>
										<AppRichTextEditor
											control={methods.control}
											name='tipsZh'
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('health.resultContent_')}
										</Typography>
										<AppRichTextEditor
											control={methods.control}
											name='contentEn'
										/>
									</Grid>
									<Grid item xs={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('health.healthTips_')}
										</Typography>
										<AppRichTextEditor
											control={methods.control}
											name='tipsEn'
										/>
									</Grid>
								</>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
